/**
 * Notifications Hooks
 *
 * React Query hooks for notification management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { type Notification, type NotificationListResponse } from '@lov/shared';
import { validatedApi } from '../../../lib/api/validated-client';

export type { Notification, NotificationListResponse };

export interface NotificationFilters {
  isRead?: boolean;
  type?: string;
  page?: number;
  limit?: number;
}

// Query keys
export const notificationKeys = {
  all: ['notifications'] as const,
  lists: () => [...notificationKeys.all, 'list'] as const,
  list: (filters: NotificationFilters) => [...notificationKeys.lists(), filters] as const,
  unreadCount: () => [...notificationKeys.all, 'unread-count'] as const,
  grouped: () => [...notificationKeys.all, 'grouped'] as const,
  detail: (id: string) => [...notificationKeys.all, 'detail', id] as const,
};

/**
 * Fetch notifications with pagination and filters
 */
export function useNotifications(filters: NotificationFilters = {}) {
  return useQuery({
    queryKey: notificationKeys.list(filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.isRead !== undefined) params.append('isRead', String(filters.isRead));
      if (filters.type) params.append('type', filters.type);
      if (filters.page) params.append('page', String(filters.page));
      if (filters.limit) params.append('limit', String(filters.limit));

      return validatedApi.get<NotificationListResponse>(`/notifications?${params.toString()}`);
    },
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // Refetch every minute
  });
}

/**
 * Get unread notification count
 */
export function useUnreadCount() {
  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: async () => {
      return validatedApi.get<{ unreadCount: number }>('/notifications/unread-count');
    },
    select: (data) => data.unreadCount,
    staleTime: 30 * 1000,
    refetchInterval: 30 * 1000, // Check every 30 seconds
  });
}

/**
 * Get notifications grouped by date
 */
export function useGroupedNotifications() {
  return useQuery({
    queryKey: notificationKeys.grouped(),
    queryFn: async () => {
      return validatedApi.get<Record<string, Notification[]>>('/notifications/grouped');
    },
    staleTime: 30 * 1000,
  });
}

/**
 * Mark a notification as read
 */
export function useMarkAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      return validatedApi.patch<Notification>(`/notifications/${notificationId}/read`);
    },
    onSuccess: () => {
      // Invalidate all notification queries
      void queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

/**
 * Mark multiple notifications as read
 */
export function useMarkMultipleAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationIds: string[]) => {
      return validatedApi.post<{ count: number }>('/notifications/read-multiple', {
        notificationIds,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

/**
 * Mark all notifications as read
 */
export function useMarkAllAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      return validatedApi.patch<{ count: number }>('/notifications/mark-all-read');
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

/**
 * Delete a notification
 */
export function useDeleteNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      await validatedApi.delete(`/notifications/${notificationId}`);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}
